import { TFunction } from 'i18next'
import { Notification } from '@/types'

/**
 * Format currency amount with proper formatting
 */
function formatCurrency(amount: number | string, currency: string = 'EUR'): string {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(numAmount)) return `${amount} ${currency}`
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount)
}

/**
 * Get translated notification title and message from code and params
 */
export function getNotificationText(
  notification: Notification,
  t: TFunction
): { title: string; message: string } {
  // If code is provided, use code-based translation
  if (notification.code) {
    const params = notification.params || {}
    const code = notification.code

    // Get translation key
    const titleKey = `notifications.codes.${code}.title`
    const messageKey = `notifications.codes.${code}.message`

    // Try to get translated title and message
    let title = t(titleKey, { defaultValue: code })
    let message = t(messageKey, { defaultValue: '' })

    // If translation not found, use fallback
    if (title === titleKey || title === code) {
      title = t(`notifications.${code.split('.').pop()}`) || code
    }
    if (message === messageKey || message === '') {
      message = t(`notifications.${code.split('.').pop()}`) || ''
    }

    // Replace params in message
    if (params.amount !== undefined && params.currency !== undefined) {
      const formattedAmount = formatCurrency(params.amount, params.currency)
      message = message.replace(/\{amount\}/g, formattedAmount)
      message = message.replace(/\{currency\}/g, params.currency)
    } else if (params.amount !== undefined) {
      message = message.replace(/\{amount\}/g, String(params.amount))
    }
    if (params.currency) {
      message = message.replace(/\{currency\}/g, params.currency)
    }
    if (params.sender_name) {
      message = message.replace(/\{sender_name\}/g, params.sender_name)
    }
    if (params.receiver_name) {
      message = message.replace(/\{receiver_name\}/g, params.receiver_name)
    }
    
    // Handle description with proper formatting
    if (params.description) {
      const descriptionText = params.description.trim()
      if (descriptionText) {
        message = message.replace(
          /\{description\}/g, 
          `. ${descriptionText}` // Add period and space before description
        )
      } else {
        message = message.replace(/\{description\}/g, '')
      }
    } else {
      message = message.replace(/\{description\}/g, '')
    }

    // Replace any remaining params
    Object.keys(params).forEach(key => {
      const value = params[key]
      if (value !== undefined && value !== null && key !== 'description') {
        message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value))
      }
    })

    return { title, message }
  }

  // Fallback to legacy title/message
  return {
    title: notification.title || t('notifications.noTitle') || 'Notification',
    message: notification.message || t('notifications.noMessage') || '',
  }
}

